import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, Row, Col, Button, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clinicService } from '../services';
import { MedicalFacility } from '../types';
import dayjs from 'dayjs';
import ProvinceSelector from './ProvinceSelector';
import WardSelector from './WardSelector';
import './MedicalFacilityModal.css';

const { TextArea } = Input;
const { Option } = Select;

interface MedicalFacilityModalProps {
  visible: boolean;
  facility?: MedicalFacility | null;
  onClose: () => void;
}

const MedicalFacilityModal: React.FC<MedicalFacilityModalProps> = ({ visible, facility, onClose }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const [currentProvinceCode, setCurrentProvinceCode] = useState<string | undefined>(undefined);

  const saveMutation = useMutation({
    mutationFn: (values: any) => {
      if (facility) {
        return clinicService.update(facility.id, values);
      } else {
        return clinicService.create(values);
      }
    },
    onSuccess: () => {
      message.success(facility ? 'Cập nhật cơ sở y tế thành công!' : 'Thêm cơ sở y tế thành công!');
      queryClient.invalidateQueries({ queryKey: ['medical-facilities'] });
      onClose();
      form.resetFields();
    },
    onError: () => {
      message.error(facility ? 'Cập nhật cơ sở y tế thất bại!' : 'Thêm cơ sở y tế thất bại!');
    },
  });

  useEffect(() => {
    if (visible) {
      if (facility) {
        // Calculate term from start_date and end_date
        let term = '5'; // default
        if (facility.start_date && facility.end_date) {
          const years = dayjs(facility.end_date).diff(dayjs(facility.start_date), 'year');
          term = years.toString();
        }
        
        form.setFieldsValue({
          ...facility,
          start_date: facility.start_date ? dayjs(facility.start_date) : null,
          term: term,
        });
        setCurrentProvinceCode(facility.province_code);
      } else {
        form.resetFields();
        form.setFieldsValue({
          start_date: dayjs(),
        });
      }
    }
  }, [visible, facility, form]);

  // Reset ward_code when province_code changes
  const handleProvinceChange = (value: string) => {
    setCurrentProvinceCode(value);
    form.setFieldsValue({ ward_code: undefined });
    form.setFieldValue('province_code', value);
  };

  const onFinish = (values: any) => {
    // Calculate end_date based on start_date and term (if term exists)
    let endDate = null;
    if (values.start_date && values.term) {
      const years = parseInt(values.term);
      endDate = values.start_date.add(years, 'year').format('YYYY-MM-DD');
    } else if (values.end_date) {
      // If end_date is provided directly, use it
      endDate = values.end_date.format('YYYY-MM-DD');
    } else if (values.start_date) {
      // Default to 5 years if term not provided
      endDate = values.start_date.add(5, 'year').format('YYYY-MM-DD');
    }

    // Only submit fields that match the API spec
    const submitData: Partial<MedicalFacility> = {
      name: values.name,
      username: values.username,
      password: values.password,
      tax_code: values.tax_code,
      representative: values.representative,
      phone: values.phone,
      email: values.email,
      province_code: values.province_code,
      ward_code: values.ward_code,
      hamlet: values.hamlet,
      street: values.street,
      start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
      end_date: endDate,
      is_active: values.is_active !== undefined ? values.is_active : true,
    };
    
    saveMutation.mutate(submitData);
  };


  const hamletOptions = [
    { value: 'khu-pho-1', label: 'Khu phố 1' },
    { value: 'khu-pho-2', label: 'Khu phố 2' },
    { value: 'khu-pho-3', label: 'Khu phố 3' },
  ];

  const termOptions = [
    { value: '1', label: '1 năm' },
    { value: '2', label: '2 năm' },
    { value: '3', label: '3 năm' },
    { value: '5', label: '5 năm' },
    { value: '10', label: '10 năm' },
  ];


  return (
    <Modal
    //   title={facility ? 'Sửa thông tin cơ sở y tế' : 'Thêm mới cơ sở y tế'}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      className="medical-facility-modal"
    >
      <Form
        form={form}
        layout="horizontal"
        onFinish={onFinish}
        initialValues={{
          start_date: dayjs('2025-12-18'),
        }}
      >
        {/* Thông tin tài khoản */}
        <div className="form-section">
          <h3 className="section-title">Thông tin tài khoản</h3>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="username"
                labelCol={{ span: 9 }}
                labelAlign="left"
                label="Username"
                rules={[{ required: true, message: 'Vui lòng nhập username!' }]}
              >
                <Input placeholder="Nhập username" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="password"
                labelCol={{ span: 9 }}
                labelAlign="left"
                label="Mật khẩu"
                rules={[{ required: !facility, message: 'Vui lòng nhập mật khẩu!' }]}
              >
                <Input.Password placeholder="Mật khẩu" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Thông tin cơ sở y tế */}
        <div className="form-section">
          <h3 className="section-title">Thông tin cơ sở y tế</h3>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Tên CSYT"

                labelCol={{ span: 6 }}
                wrapperCol={{ span: 20 }}
                labelAlign="left"
                rules={[{ required: true, message: 'Vui lòng nhập tên cơ sở y tế!' }]}
              >
                <Input placeholder="Nhập tên cơ sở y tế" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="tax_code"
                label="Mã số thuế"
                labelCol={{ span: 6 }}
                labelAlign="left"
                rules={[{ required: true, message: 'Vui lòng nhập mã số thuế!' }]}
              >
                <Input placeholder="Nhập mã số thuế" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="province_code"
                label="Tỉnh/Thành"
                labelCol={{ span: 9 }}
                labelAlign="left"
                rules={[{ required: true, message: 'Vui lòng chọn tỉnh/thành!' }]}
              >
                <ProvinceSelector onChange={handleProvinceChange} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="ward_code"
                label="Phường/Xã"
                labelCol={{ span: 9 }}
                labelAlign="left"
                rules={[{ required: true, message: 'Vui lòng chọn phường/xã!' }]}
              >
               <WardSelector provinceCode={currentProvinceCode} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="hamlet" label="Khu phố / ấp" labelCol={{ span: 9 }} labelAlign="left">
                <Select placeholder="Chọn">
                  {hamletOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="street"
                label="Địa chỉ"
                labelCol={{ span: 3 }}
                labelAlign="left"
                rules={[{ required: true, message: 'Vui lòng nhập địa chỉ!' }]}
              >
                <Input placeholder="Số nhà, tên đường" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="representative"
                label="Người đại diện"
                labelCol={{ span: 9 }}
                labelAlign="left"
                rules={[{ required: true, message: 'Vui lòng nhập người đại diện!' }]}
              >
                <Input placeholder="Nhập tên người đại diện" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="phone"
                label="Điện thoại"
                labelCol={{ span: 9 }}
                labelAlign="left"
                rules={[{ required: true, message: 'Vui lòng nhập số điện thoại!' }]}
              >
                <Input placeholder="Nhập số điện thoại" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="email" label="Email" labelCol={{ span: 9 }} labelAlign="left">
                <Input type="email" placeholder="Nhập email" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="start_date"
                label="Ngày bắt đầu"
                labelCol={{ span: 9 }}
                labelAlign="left"
                rules={[{ required: true, message: 'Vui lòng chọn ngày bắt đầu!' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Chọn ngày bắt đầu"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="term"
                label="Thời hạn"
                labelCol={{ span: 9 }}
                labelAlign="left"
                rules={[{ required: true, message: 'Vui lòng chọn thời hạn!' }]}
              >
                <Select placeholder="Chọn thời hạn">
                  {termOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="notes" label="Ghi chú" labelCol={{ span: 3 }} labelAlign="left">
                <TextArea rows={4} placeholder="Nhập ghi chú (nếu có)" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Action buttons */}
        <div className="form-actions">
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Hủy
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={saveMutation.isPending}
          >
            {facility ? 'Cập nhật' : 'Thêm mới'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default MedicalFacilityModal;

